import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import AuthContext from '../../context/authContext';
import { useAgentReports } from '../../hooks/useAgentReports';
import { useEmail } from '../../hooks/useEmail';
import { toast } from 'react-toastify';
import { getRenderableEmailHtml } from '../../util/emailContent';

dayjs.locale('de');

const STATUS_TABS = [
  { key: 'open', label: 'Offen' },
  { key: 'reviewed', label: 'In Prüfung' },
  { key: 'resolved', label: 'Erledigt' },
];

const statusChipColor = status => {
  switch (status) {
    case 'resolved':
      return 'success';
    case 'reviewed':
      return 'warning';
    default:
      return 'info';
  }
};

export default function AgentReportedConversationsView() {
  const authCtx = useContext(AuthContext);
  const {
    listReportedConversations,
    updateReportedConversationStatus,
    getReportedChatSession,
    isLoading,
  } = useAgentReports();
  const { fetchEmailThread } = useEmail();

  if (!authCtx.checkRight('agent:reported')) {
    return <Navigate to='/welcome' replace />;
  }

  const [status, setStatus] = useState('open');
  const [reports, setReports] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const selectedReport = useMemo(
    () => reports.find(r => (r?._id || r?.id) === selectedId) || null,
    [reports, selectedId]
  );

  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [chatSession, setChatSession] = useState(null);
  const [loadingChat, setLoadingChat] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const loadReports = useCallback(async () => {
    try {
      const response = await listReportedConversations({ status, limit: 100, skip: 0 });
      setReports(Array.isArray(response?.reports) ? response.reports : []);
      if (selectedId) {
        const stillExists = (response?.reports || []).some(r => (r?._id || r?.id) === selectedId);
        if (!stillExists) {
          setSelectedId(null);
          setThread(null);
        }
      }
    } catch (e) {}
  }, [listReportedConversations, selectedId, status]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleSelect = useCallback(
    async report => {
      const id = report?._id || report?.id;
      setSelectedId(id);
      setThread(null);
      setChatSession(null);

      if (report?.kind === 'mail') {
        if (!report?.mailId) return;

        setLoadingThread(true);
        try {
          const response = await fetchEmailThread(report.mailId, { skipAiSuggestion: true });
          setThread(response);
        } catch (e) {
          toast.error('Fehler beim Laden der E-Mail-Konversation');
        } finally {
          setLoadingThread(false);
        }
        return;
      }

      if (report?.kind === 'chat') {
        if (!id) return;
        setLoadingChat(true);
        try {
          const response = await getReportedChatSession(id, { limit: 500, skip: 0 });
          setChatSession(response?.session || null);
        } catch (e) {
          // toast handled by hook
        } finally {
          setLoadingChat(false);
        }
      }
    },
    [fetchEmailThread, getReportedChatSession]
  );

  const updateStatus = useCallback(
    async nextStatus => {
      if (!selectedReport) return;
      const id = selectedReport?._id || selectedReport?.id;
      if (!id || updatingStatus) return;

      setUpdatingStatus(true);
      try {
        const response = await updateReportedConversationStatus(id, nextStatus);
        const updated = response?.report || null;
        if (updated) {
          setReports(prev =>
            prev.map(r => {
              const rid = r?._id || r?.id;
              return rid === id ? updated : r;
            })
          );
        }
        toast.success('Status aktualisiert');
      } finally {
        setUpdatingStatus(false);
      }
    },
    [selectedReport, updateReportedConversationStatus, updatingStatus]
  );

  const statusTabIndex = Math.max(
    0,
    STATUS_TABS.findIndex(t => t.key === status)
  );

  return (
    <Box p={2} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {isLoading && <CircularProgress size={18} />}
      </Box>

      <Paper sx={{ p: 1 }}>
        <Tabs
          value={statusTabIndex}
          onChange={(_, idx) => setStatus(STATUS_TABS[idx]?.key || 'open')}
          variant='scrollable'
          scrollButtons='auto'
        >
          {STATUS_TABS.map(t => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '360px 1fr' },
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        <Paper
          sx={{ p: 1, overflow: 'auto', maxHeight: { xs: 'auto', md: 'calc(100vh - 240px)' } }}
        >
          {isLoading && reports.length === 0 ? (
            <Box p={3} display='flex' justifyContent='center'>
              <CircularProgress />
            </Box>
          ) : reports.length === 0 ? (
            <Box p={2}>
              <Typography variant='body2' color='text.secondary'>
                Keine Meldungen vorhanden.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {reports.map((r, idx) => {
                const id = r?._id || r?.id || `${idx}`;
                const createdAt = r?.meta?.createdAt ? dayjs(r.meta.createdAt) : null;
                const primary =
                  r?.kind === 'mail' ? `Mail: ${r.mailId}` : `Chat: ${r.agentSessionToken || '-'}`;
                const reporterLabel =
                  r?.meta?.createdByName ||
                  (r?.meta?.createdByKind === 'customer'
                    ? 'Kunde'
                    : r?.meta?.createdByKind === 'employee'
                      ? 'Mitarbeiter'
                      : null);
                const secondaryParts = [
                  createdAt ? createdAt.format('DD.MM.YYYY HH:mm') : null,
                  reporterLabel ? `von ${reporterLabel}` : null,
                  r?.reason ? `Grund: ${r.reason}` : null,
                ].filter(Boolean);
                return (
                  <React.Fragment key={id}>
                    <ListItemButton selected={selectedId === id} onClick={() => handleSelect(r)}>
                      <ListItemText
                        primary={
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                          >
                            <Typography variant='body2' sx={{ fontWeight: 600 }}>
                              {primary}
                            </Typography>
                            <Chip
                              size='small'
                              label={r?.status || 'open'}
                              color={statusChipColor(r?.status)}
                              variant='outlined'
                            />
                          </Box>
                        }
                        secondary={
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                          >
                            {secondaryParts.join(' · ')}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Paper>

        <Paper
          sx={{ p: 2, overflow: 'auto', maxHeight: { xs: 'auto', md: 'calc(100vh - 240px)' } }}
        >
          {!selectedReport ? (
            <Typography variant='body2' color='text.secondary'>
              Wähle links eine Meldung aus.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}
              >
                <Box>
                  <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                    Details
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {selectedReport?.meta?.createdAt
                      ? dayjs(selectedReport.meta.createdAt).format('DD.MM.YYYY HH:mm')
                      : '-'}
                  </Typography>
                </Box>
                <Chip
                  size='small'
                  label={selectedReport?.status || 'open'}
                  color={statusChipColor(selectedReport?.status)}
                  variant='outlined'
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size='small'
                  variant={selectedReport?.status === 'open' ? 'contained' : 'outlined'}
                  onClick={() => updateStatus('open')}
                  disabled={updatingStatus}
                >
                  Offen
                </Button>
                <Button
                  size='small'
                  variant={selectedReport?.status === 'reviewed' ? 'contained' : 'outlined'}
                  onClick={() => updateStatus('reviewed')}
                  disabled={updatingStatus}
                  color='warning'
                >
                  In Prüfung
                </Button>
                <Button
                  size='small'
                  variant={selectedReport?.status === 'resolved' ? 'contained' : 'outlined'}
                  onClick={() => updateStatus('resolved')}
                  disabled={updatingStatus}
                  color='success'
                >
                  Erledigt
                </Button>
              </Box>

              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Grund
                </Typography>
                <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedReport?.reason || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Vorschlag
                </Typography>
                <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedReport?.suggestion?.text || '-'}
                </Typography>
              </Box>

              {selectedReport?.kind === 'mail' && (
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    E-Mail Konversation
                  </Typography>
                  {loadingThread ? (
                    <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : thread?.mails && Array.isArray(thread.mails) ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                      {thread.mails.map(mail => {
                        const key = mail?._id || mail?.id || `${Math.random()}`;
                        return (
                          <Paper key={key} sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)' }}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                              {mail?.subject || '(ohne Betreff)'}
                            </Typography>
                            <Typography variant='caption' color='text.secondary' display='block'>
                              {mail?.direction === 'inbound'
                                ? `Von: ${mail?.from}`
                                : `An: ${mail?.to}`}
                            </Typography>
                            <Typography variant='caption' color='text.secondary' display='block'>
                              {mail?.direction === 'inbound'
                                ? `An: ${mail?.to}`
                                : `Von: ${mail?.from}`}
                            </Typography>
                            <Box
                              sx={{
                                mt: 1,
                                '& .email-content a': {
                                  color: 'primary.main',
                                  textDecoration: 'underline',
                                },
                              }}
                              dangerouslySetInnerHTML={{
                                __html: `<div class="email-content">${getRenderableEmailHtml(
                                  mail
                                )}</div>`,
                              }}
                            />
                          </Paper>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                      Keine Konversation geladen.
                    </Typography>
                  )}
                </Box>
              )}

              {selectedReport?.kind === 'chat' && (
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Chat Konversation
                  </Typography>
                  {loadingChat ? (
                    <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : chatSession?.messages && Array.isArray(chatSession.messages) ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                      {chatSession.messages.length === 0 ? (
                        <Typography variant='body2' color='text.secondary'>
                          Keine Nachrichten vorhanden.
                        </Typography>
                      ) : (
                        chatSession.messages.map((m, idx) => {
                          const isUser =
                            m?.role === 'user' || m?.role === 'customer' || m?.role === 'guest';
                          const label =
                            m?.senderName ||
                            (isUser
                              ? 'Kunde'
                              : m?.role === 'assistant'
                                ? 'Assistent'
                                : m?.role || 'System');
                          return (
                            <Box
                              key={`${m?.createdAt || idx}-${idx}`}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: isUser ? 'flex-start' : 'flex-end',
                              }}
                            >
                              <Typography variant='caption' color='text.secondary'>
                                {label}
                                {m?.createdAt
                                  ? ` · ${dayjs(m.createdAt).format('DD.MM.YYYY HH:mm')}`
                                  : ''}
                              </Typography>
                              <Paper
                                sx={{
                                  mt: 0.5,
                                  p: 1.25,
                                  maxWidth: '100%',
                                  width: 'fit-content',
                                  bgcolor: isUser
                                    ? 'rgba(255,255,255,0.04)'
                                    : 'rgba(25,118,210,0.12)',
                                }}
                              >
                                <Typography
                                  variant='body2'
                                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                >
                                  {m?.content || '-'}
                                </Typography>
                              </Paper>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  ) : (
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                      Keine Chat-Konversation geladen.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
